import React, { useState, useEffect, useRef } from 'react';
import { Smartphone, Menu, X, ChevronDown } from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';

const translations = {
  pt: {
    home: 'Início',
    features: 'Recursos',
    games: 'Jogos',
    contact: 'Contato',
  },
  en: {
    home: 'Home',
    features: 'Features',
    games: 'Games',
    contact: 'Contact',
  }
};

function Header({ language, onLanguageChange }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const t = translations[language];

  const languages = [
    { code: 'pt', name: 'Português', flag: 'PT' },
    { code: 'en', name: 'English', flag: 'GB' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-darker/98 backdrop-blur-xl shadow-2xl shadow-primary/20 border-b border-primary/20' 
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <div className="flex items-center gap-4 cursor-pointer group" onClick={() => scrollToSection('hero')}>
            <div className="relative">
              <div className="w-14 h-14 bg-gradient-to-br from-primary via-secondary to-accent rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-purple-glow">
                <Smartphone className="w-8 h-8 text-white" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-primary to-accent rounded-2xl blur-xl opacity-60 group-hover:opacity-80 transition-opacity"></div>
            </div>
            <div>
              <span className="font-heading text-2xl font-bold text-white tracking-wider block">
                Mundo<span className="gradient-text">Digital</span>24
              </span>
              <span className="text-xs text-accent font-body tracking-widest">Premium Mobile Games</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-2">
            {[
              { key: 'home', id: 'hero' },
              { key: 'features', id: 'features' },
              { key: 'games', id: 'games' },
              { key: 'contact', id: 'footer' },
            ].map((item) => (
              <button
                key={item.key}
                onClick={() => scrollToSection(item.id)}
                className="relative px-6 py-3 text-slate-300 hover:text-white font-body text-base font-semibold tracking-wide transition-all duration-300 group"
              >
                {t[item.key]}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary via-secondary to-accent group-hover:w-full transition-all duration-300"></span>
                <span className="absolute inset-0 bg-primary/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"></span>
              </button>
            ))}
          </nav>

          {/* Language Switcher Dropdown */}
          <div className="flex items-center gap-4">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-5 py-2.5 bg-surface/80 backdrop-blur-sm rounded-xl border border-primary/30 shadow-lg text-white font-body font-bold text-sm transition-all duration-300 hover:bg-primary/10 hover:border-primary/50"
              >
                <ReactCountryFlag
                  countryCode={currentLanguage.flag}
                  svg
                  style={{
                    width: '1.3em',
                    height: '1.3em',
                    borderRadius: '3px',
                  }}
                  title={currentLanguage.name}
                />
                <span>{currentLanguage.code.toUpperCase()}</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-surface/95 backdrop-blur-xl rounded-xl border border-primary/30 shadow-purple-glow overflow-hidden z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        onLanguageChange(lang.code);
                        setDropdownOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-200 ${
                        language === lang.code
                          ? 'bg-gradient-to-r from-primary/20 to-secondary/20 text-white'
                          : 'text-slate-300 hover:text-white hover:bg-primary/10'
                      }`}
                    >
                      <ReactCountryFlag
                        countryCode={lang.flag}
                        svg
                        style={{
                          width: '1.3em',
                          height: '1.3em',
                          borderRadius: '3px',
                        }}
                        title={lang.name}
                      />
                      <span className="font-body font-semibold">{lang.name}</span>
                      {language === lang.code && (
                        <span className="ml-auto text-accent">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-white hover:bg-primary/20 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`md:hidden transition-all duration-300 overflow-hidden ${
        mobileMenuOpen ? 'max-h-96' : 'max-h-0'
      }`}>
        <nav className="bg-surface/98 backdrop-blur-xl border-t border-primary/20 px-4 py-4 space-y-2">
          {[
            { key: 'home', id: 'hero' },
            { key: 'features', id: 'features' },
            { key: 'games', id: 'games' },
            { key: 'contact', id: 'footer' },
          ].map((item) => (
            <button
              key={item.key}
              onClick={() => scrollToSection(item.id)}
              className="block w-full text-left text-slate-300 hover:text-white font-body text-lg py-3 px-4 rounded-lg hover:bg-primary/20 transition-all"
            >
              {t[item.key]}
            </button>
          ))}
        </nav>
      </div>
    </header>
  );
}

export default Header;
